// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { type ImportEntry, useImportsListQuery } from "@/api/hooks/use-imports";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { ImportPlusIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { ImportDataDialog } from "@/features/imports/import-data-dialog";
import { ImportsTable } from "@/features/imports/imports-table";
import { ImportsToolsMenu } from "@/features/imports/imports-tools-menu";
import { ManualSftpImportDialog } from "@/features/imports/manual-sftp-import-dialog";
import { useImportsFiltersState } from "@/features/imports/use-imports-filters-state";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { formatDuration, formatIsoDateTime } from "@/lib/format";

interface ChainImportsTabProps {
  chainName: string;
  initialFilters?: string;
}

export function ChainImportsTab({ chainName, initialFilters }: ChainImportsTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.ingressImportsEdit);

  const [importDataOpen, setImportDataOpen] = useState(false);
  const [manualSftpOpen, setManualSftpOpen] = useState(false);

  const onSync = useCallback(
    (encoded: string | null) => {
      navigate({
        to: "/chains/$chainName",
        params: { chainName },
        search: { t: "imports", ...(encoded ? { f: encoded } : {}) },
        replace: true,
      });
    },
    [navigate, chainName],
  );

  const filters = useImportsFiltersState(initialFilters, onSync);
  const {
    pagination,
    setPagination,
    sorting,
    setSorting,
    search,
    setSearch,
    storeFilter,
    setStoreFilter,
    importTypeFilter,
    setImportTypeFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    resetToFirstPage,
  } = filters;

  // Fetch the chain's full store list so the Store filter and column link work even before
  // the first imports page lands (and survive filtering, which trims the imports response's
  // own `stores` array).
  const { data: chainStoresData } = useStoresQuery({ chainName, limit: -1, onlyActive: false });
  const chainStores = useMemo(
    () =>
      (chainStoresData?.stores ?? []).map((s) => ({
        storeId: s.storeId,
        storeName: s.storeName,
        storeDisplay: s.legibleName,
      })),
    [chainStoresData?.stores],
  );

  const { data, isLoading, refetch, isFetching } = useImportsListQuery({
    chainName,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    sortBy: "id",
    orderBy: "desc",
    search,
    storeName: storeFilter,
    importType: importTypeFilter,
    source: sourceFilter,
    status: statusFilter,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/ingress/imports", {
      params: {
        query: {
          page: 1,
          limit: -1,
          sortBy: "id",
          orderBy: "desc",
          "filter[chainName]": chainName,
        },
      },
    });
    const items = (res.data as unknown as { imports: ImportEntry[] }).imports;
    downloadCsv(
      `chain-${chainName}-imports.csv`,
      ["ID", "Type", "Store Code", "Source", "Status", "Uploaded By", "Processing Time", "Created"],
      items.map((i) => [
        String(i.importRequestId),
        i.importType,
        i.storeCode ?? "",
        i.source ?? "api",
        i.status ?? "",
        String(i.createdBy ?? ""),
        i.processingTime != null ? formatDuration(i.processingTime) : "",
        formatIsoDateTime(i.createdAt),
      ]),
    );
  }, [chainName]);

  return (
    <div className="p-6">
      <ImportsTable
        scope="chain"
        storageKey="chain-imports"
        imports={data?.imports ?? []}
        count={data?.count ?? 0}
        isLoading={isLoading}
        isFetching={isFetching}
        refetch={refetch}
        stores={chainStores}
        users={data?.users}
        vendors={data?.vendors}
        canEdit={canEdit}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          resetToFirstPage();
        }}
        storeFilter={storeFilter}
        onStoreFilterChange={(value) => {
          setStoreFilter(value);
          resetToFirstPage();
        }}
        importTypeFilter={importTypeFilter}
        onImportTypeFilterChange={(value) => {
          setImportTypeFilter(value);
          resetToFirstPage();
        }}
        sourceFilter={sourceFilter}
        onSourceFilterChange={(value) => {
          setSourceFilter(value);
          resetToFirstPage();
        }}
        statusFilter={statusFilter}
        onStatusFilterChange={(value) => {
          setStatusFilter(value);
          resetToFirstPage();
        }}
        onExport={handleExport}
        toolbar={
          <>
            <ImportsToolsMenu>
              <DropdownMenuItem onSelect={() => setManualSftpOpen(true)}>
                {t("imports.manualSftp.menuItem")}
              </DropdownMenuItem>
            </ImportsToolsMenu>
            <Button onClick={() => setImportDataOpen(true)} disabled={!canEdit}>
              <ImportPlusIcon className="h-4 w-4" />
              {t("imports.importData")}
            </Button>
          </>
        }
      />
      <ImportDataDialog open={importDataOpen} onOpenChange={setImportDataOpen} chainName={chainName} />
      <ManualSftpImportDialog
        open={manualSftpOpen}
        onOpenChange={setManualSftpOpen}
        chainName={chainName}
        canEdit={canEdit}
      />
    </div>
  );
}
